import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Sparkles, ChevronDown } from 'lucide-react';

const APK_VERSION = '1.0.39';

// "ClayPulse Application" dropdown — homepage anchors only.
const appDropdown = [
  { label: 'Fonctionnalités', href: '#fonctionnalites' },
  { label: 'Disciplines', href: '#disciplines' },
  { label: 'Tarifs', href: '#tarifs' },
  { label: 'FAQ', href: '#faq' },
];

// Standalone top-level links (kept as separate menu entries).
const standaloneLinks = [
  { label: 'Clayo (IA)', href: '/clayo', isPage: true },
  { label: 'La carte du tireur sportif', href: '/ball-trap-campagne', isPage: true },
  { label: 'Mise à jour Android', href: '/android', isPage: true, hasNotif: true },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [bannerVisible, setBannerVisible] = useState(true);
  const [appDropdownOpen, setAppDropdownOpen] = useState(false);
  const [mobileAppOpen, setMobileAppOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setAppDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const handleClick = (href, isPage) => {
    setMobileOpen(false);
    setAppDropdownOpen(false);
    setMobileAppOpen(false);
    if (isPage) {
      window.location.href = href;
      return;
    }
    // Anchor on home page — if not on home, navigate to /#anchor
    if (window.location.pathname !== '/') {
      window.location.href = '/' + href;
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* Update banner */}
      <AnimatePresence>
        {bannerVisible && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed top-0 left-0 right-0 z-[60] bg-gradient-to-r from-[#FF6B35] to-[#FF8A5B] overflow-hidden"
          >
            <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-3 relative">
              <Sparkles size={14} className="text-white/90 shrink-0" />
              <a
                href="/android"
                data-testid="update-banner-link"
                className="text-white text-xs sm:text-sm font-medium hover:underline"
              >
                Nouvelle version disponible : <strong>v{APK_VERSION}</strong> — Mettre à jour maintenant
              </a>
              <button
                onClick={(e) => { e.stopPropagation(); setBannerVisible(false); }}
                data-testid="update-banner-close"
                className="absolute right-4 text-white/80 hover:text-white"
              >
                <X size={14} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.nav
        data-testid="navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed left-0 right-0 z-50 transition-all duration-500 ${
          bannerVisible ? 'top-[32px]' : 'top-0'
        } ${
          scrolled
            ? 'bg-[#0D0D0D]/90 backdrop-blur-xl border-b border-white/5 shadow-lg'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a href="/" data-testid="navbar-logo" className="flex items-center gap-2 group">
            <img
              src="/assets/img/logo-claypulse.jpg"
              alt="ClayPulse"
              className="w-10 h-10 rounded-lg object-cover"
            />
            <span className="text-white font-bold text-xl font-['Poppins'] tracking-tight group-hover:text-[#FF6B35] transition-colors duration-300">
              Clay<span className="text-[#FF6B35] group-hover:text-white transition-colors duration-300">Pulse</span>
            </span>
          </a>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-8">
            {/* ClayPulse Application — dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                data-testid="nav-app-dropdown-toggle"
                onClick={() => setAppDropdownOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 text-[#A0A0A0] hover:text-white text-sm font-medium transition-colors duration-300"
              >
                ClayPulse Application
                <ChevronDown
                  size={14}
                  className={`transition-transform duration-300 ${appDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>
              <AnimatePresence>
                {appDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.18 }}
                    data-testid="nav-app-dropdown-menu"
                    className="absolute top-full left-0 mt-3 min-w-[220px] rounded-xl bg-[#0D0D0D]/95 backdrop-blur-xl border border-white/10 shadow-2xl overflow-hidden"
                  >
                    {appDropdown.map((link) => (
                      <button
                        key={link.href}
                        data-testid={`nav-app-item-${link.href.replace('#', '')}`}
                        onClick={() => handleClick(link.href, link.isPage)}
                        className="w-full text-left px-4 py-2.5 text-sm text-[#A0A0A0] hover:text-white hover:bg-white/5 transition-colors"
                      >
                        {link.label}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Standalone links */}
            {standaloneLinks.map((link) => (
              <button
                key={link.href}
                data-testid={`nav-link-${link.href.replace('/', '')}`}
                onClick={() => handleClick(link.href, link.isPage)}
                className="relative text-[#A0A0A0] hover:text-white text-sm font-medium transition-colors duration-300 after:content-[''] after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-[#FF6B35] after:transition-all after:duration-300 hover:after:w-full"
              >
                {link.label}
                {link.hasNotif && (
                  <span className="absolute -top-2 -right-3 flex h-4 w-4">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF6B35] opacity-75" />
                    <span className="relative inline-flex rounded-full h-4 w-4 bg-[#FF6B35] items-center justify-center text-[8px] text-white font-bold">!</span>
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Desktop CTA */}
          <a
            href="#telechargement"
            data-testid="navbar-cta"
            onClick={(e) => { e.preventDefault(); handleClick('#cta-final'); }}
            className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#FF8A5B] text-white text-sm font-semibold hover:shadow-[0_0_25px_rgba(255,107,53,0.3)] transition-all duration-300 hover:scale-105"
          >
            Télécharger
          </a>

          {/* Mobile burger */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden text-white p-2"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-[#0D0D0D]/95 backdrop-blur-xl border-t border-white/5 overflow-hidden"
            >
              <div className="px-6 py-6 flex flex-col gap-1">
                {/* Mobile: ClayPulse Application accordion */}
                <button
                  data-testid="mobile-app-dropdown-toggle"
                  onClick={() => setMobileAppOpen((v) => !v)}
                  className="flex items-center justify-between text-white text-left text-base font-semibold py-3 transition-colors"
                >
                  ClayPulse Application
                  <ChevronDown
                    size={18}
                    className={`transition-transform duration-300 ${mobileAppOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                <AnimatePresence>
                  {mobileAppOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden pl-3 border-l border-white/10 ml-1"
                    >
                      {appDropdown.map((link) => (
                        <button
                          key={link.href}
                          onClick={() => handleClick(link.href, link.isPage)}
                          className="block w-full text-left text-[#A0A0A0] hover:text-white text-sm py-2 transition-colors"
                        >
                          {link.label}
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>

                {standaloneLinks.map((link) => (
                  <button
                    key={link.href}
                    onClick={() => handleClick(link.href, link.isPage)}
                    className="text-[#A0A0A0] hover:text-white text-left text-base font-medium py-3 transition-colors"
                  >
                    {link.label}
                  </button>
                ))}

                <a
                  href="#cta-final"
                  onClick={(e) => { e.preventDefault(); handleClick('#cta-final'); }}
                  className="mt-3 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-gradient-to-r from-[#FF6B35] to-[#FF8A5B] text-white font-semibold"
                >
                  Télécharger
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  );
}
